import * as React from 'react';
import * as classNames from 'classnames';
import { action, observable } from 'mobx';
import { observer, inject } from 'mobx-react';
import { CSSTransitionGroup } from 'react-transition-group';
import { IAppOnlyStore } from '../../stores';
import { VendorPicker } from '../../components/vendor';
import { WarehousePicker } from '../../components/warehouse';
import { Button, EButtonType } from '../../components/action';
import { ShippingListUploadForm, ShippingListUploadValidation } from '.';
import { EShippingListUploadStatus, EShippingListUploadProgress } from '../../stores/ShippingListUpload';
import { EShippingListDataFilter } from '../../../../common/interfaces/shippingList';
import { Pages, PageBar } from '../../components/layout';
import { Tooltip } from '../../components/popup';
import * as zenscroll from 'zenscroll';

// Styles
const s = require('./ShippingListUpload.css');
const dropdownIcon = require('./../../assets/dropdown-arrow-revised.svg');

// Component
@inject('store')
@observer
export class ShippingListUpload extends React.Component<IAppOnlyStore, {}> {
    constructor(props: IAppOnlyStore) {
        super(props);

        this.props.store.shippingListUpload.initialize();
    }

    @observable private filterDropdownOpen: boolean = false;
    @observable private filterDropdownPositionFromLeft: number = 0;

    private filterButton: Button = null;
    private referenceFilterButton = ref => (this.filterButton = ref);

    render() {
        const { shippingListUpload } = this.props.store;

        return (
            <div>
                <PageBar className={s.topbar} visible={shippingListUpload.userIsAfterVendorStep}>
                    {(shippingListUpload.userIsAfterVendorStep && (
                        <div key="summary">
                            <Button
                                className={s.cancelButton}
                                onClick={this.handleResetting}
                                inBox={true}
                                type={EButtonType.ImportantBorderOnly}
                            >
                                Cancel
                            </Button>

                            <Button
                                className={s.backButton}
                                onClick={this.handleGoingStepBack}
                                inBox={true}
                                type={EButtonType.BorderOnly}
                            >
                                Back
                            </Button>

                            <p className={s.selection}>
                                {shippingListUpload.userIsAfterVendorStep && <span>Vendor: </span>}
                                {shippingListUpload.userIsAfterVendorStep && (
                                    <strong>{shippingListUpload.selectedVendorCardCode}</strong>
                                )}
                                {shippingListUpload.userIsAfterWarehouseStep && <span>Warehouse: </span>}
                                {shippingListUpload.userIsAfterWarehouseStep && (
                                    <strong>{shippingListUpload.selectedWarehouseCode}</strong>
                                )}
                            </p>

                            {shippingListUpload.userIsOnCsvUploadStep && (
                                <a className={s.downloadLink} href="/csv/shipping-list-upload.csv">
                                    Download template file
                                </a>
                            )}
                        </div>
                    )) || <div key="summary-filler" />}

                    {(shippingListUpload.userIsOnCsvValidationStep && (
                        <div key="finalize">
                            {(!shippingListUpload.uploadingCsvFile &&
                                !shippingListUpload.allEntriesAreValid && (
                                    <p className={classNames(s.notice, s.error)}>
                                        {`Some uploaded items have${
                                            shippingListUpload.anyEntryHasDuplicateItemsPriceMismatch ? '' : ' higher'
                                        }
                                    `}
                                        <br />
                                        {shippingListUpload.anyEntryHasDuplicateItemsPriceMismatch
                                            ? 'different prices on same items'
                                            : 'shipment quantity than open quantity'}
                                    </p>
                                )) ||
                                (!shippingListUpload.uploadingCsvFile &&
                                    shippingListUpload.anyEntryHasPriceMismatch && (
                                        <p className={classNames(s.notice, s.highlight)}>
                                            Some upload items price<br />does not match SAP vendor pice list
                                        </p>
                                    ))}

                            <Button
                                ref={this.referenceFilterButton}
                                className={s.filterButton}
                                onClick={this.handleToggleFilterDropdown()}
                                inBox={true}
                                icon={{
                                    element: <img src={dropdownIcon} width={10} height={7} alt="" />
                                }}
                                type={
                                    !shippingListUpload.uploadingCsvFile && !shippingListUpload.allEntriesAreValid
                                        ? EButtonType.Default
                                        : shippingListUpload.shippingListDataFilter === EShippingListDataFilter.All
                                          ? EButtonType.BorderOnly
                                          : EButtonType.Default
                                }
                            >
                                {shippingListUpload.shippingListDataFilter === EShippingListDataFilter.All
                                    ? 'Filter: showing all CSV data'
                                    : shippingListUpload.shippingListDataFilter === EShippingListDataFilter.PriceDifferences
                                      ? 'Filter: showing price mismatch data'
                                      : shippingListUpload.shippingListDataFilter ===
                                        EShippingListDataFilter.QuantityDifferences
                                        ? 'Filter: showing quantity mismatch data'
                                        : 'Filter data'}
                            </Button>

                            {shippingListUpload.allEntriesAreValid && (
                                <Button
                                    className={s.submitButton}
                                    onClick={this.handleSubmit}
                                    inBox={true}
                                    type={
                                        shippingListUpload.shippingListSaveProgress === EShippingListUploadProgress.Error
                                            ? EButtonType.Important
                                            : shippingListUpload.shippingListSaveProgress ===
                                              EShippingListUploadProgress.Success
                                              ? EButtonType.Success
                                              : EButtonType.Action
                                    }
                                >
                                    {shippingListUpload.shippingListSaveProgress === EShippingListUploadProgress.Saving
                                        ? 'Processing'
                                        : shippingListUpload.shippingListSaveProgress === EShippingListUploadProgress.Error
                                          ? 'Could not process, try again'
                                          : shippingListUpload.shippingListSaveProgress === EShippingListUploadProgress.Success
                                            ? 'Changes uploaded successfully, start next upload'
                                            : 'Submit'}
                                </Button>
                            )}
                        </div>
                    )) || <div key="finalize-filler" />}
                </PageBar>

                <Pages>
                    {shippingListUpload.status === EShippingListUploadStatus.VendorSearch && (
                        <VendorPicker
                            key="vendor-picker"
                            allVendorsOrPOVendors="po"
                            onSelectedVendorChange={this.handleVendorChange}
                            selectedVendorCardCode={shippingListUpload.selectedVendorCardCode}
                        />
                    )}

                    {shippingListUpload.status === EShippingListUploadStatus.WarehouseSearch && (
                        <WarehousePicker
                            key="warehouse-picker"
                            selectedWarehouseCode={shippingListUpload.selectedWarehouseCode}
                            onSelectedWarehouseChange={this.handleWarehouseChange}
                        />
                    )}

                    {shippingListUpload.status === EShippingListUploadStatus.CSVUpload && (
                        <ShippingListUploadForm key="csv-upload" onFilesChange={this.handleCsvFileChange} />
                    )}

                    {shippingListUpload.status === EShippingListUploadStatus.CSVSubmission && (
                        <ShippingListUploadValidation key="csv-validation" />
                    )}
                </Pages>

                <Tooltip
                    className={s.filtersDropdown}
                    style={{ left: this.filterDropdownPositionFromLeft + 'px' }}
                    onClose={this.handleCloseFilterDropdown}
                    show={this.filterDropdownOpen}
                    title="Filter CSV data"
                >
                    {Object.keys(EShippingListDataFilter).map(key => {
                        const filter = EShippingListDataFilter[key];
                        return (
                            <Button
                                key={key}
                                className={s.filtersDropdownButton}
                                onClick={this.handleFilteringChange(filter)}
                                type={
                                    shippingListUpload.shippingListDataFilter === filter
                                        ? EButtonType.Default
                                        : EButtonType.Action
                                }
                            >
                                {filter}
                            </Button>
                        );
                    })}
                </Tooltip>
            </div>
        );
    }

    @action
    private handleResetting = () => {
        if (this.props.store.shippingListUpload.shippingListSaveProgress !== EShippingListUploadProgress.Saving) {
            this.props.store.shippingListUpload.initialize();
        }
    };

    @action
    private handleGoingStepBack = () => {
        this.props.store.shippingListUpload.goStepBack();
    };

    /**
     * Opens or closes filter dropdown
     *
     * @param {boolean} [isOpen] if isOpen param is skipped action will act as a toggle
     */
    @action
    private handleToggleFilterDropdown = (isOpen?: boolean) => (e: React.MouseEvent<HTMLButtonElement>) => {
        if ((typeof isOpen !== 'undefined' && isOpen) || !this.filterDropdownOpen) {
            this.filterDropdownOpen = true;

            if (this.filterButton && this.filterButton.container) {
                this.filterDropdownPositionFromLeft = this.filterButton.container.offsetLeft;
            }
        } else {
            this.filterDropdownOpen = false;
        }
    };

    @action
    private handleCloseFilterDropdown = () => {
        this.filterDropdownOpen = false;
    };

    @action
    private handleFilteringChange = (filter: string) => (e: React.MouseEvent<HTMLButtonElement>) => {
        this.props.store.shippingListUpload.shippingListDataFilter = filter as EShippingListDataFilter;
        this.filterDropdownOpen = false;
        zenscroll.toY(0);
    };

    @action
    private handleVendorChange = (cardCode: string = null) => {
        this.props.store.shippingListUpload.changeSelectedVendorCardCode(cardCode);
    };

    @action
    private handleWarehouseChange = (whsCode: string = null) => {
        this.props.store.shippingListUpload.changeSelectedWarehouseCode(whsCode);
    };

    @action
    private handleCsvFileChange = (files: FileList) => {
        this.props.store.shippingListUpload.changeSelectedFileList(files);
    };

    @action
    private handleSubmit = () => {
        if (this.props.store.shippingListUpload.shippingListSaveProgress === EShippingListUploadProgress.Success) {
            this.props.store.shippingListUpload.initialize();
        } else if (this.props.store.shippingListUpload.shippingListSaveProgress !== EShippingListUploadProgress.Saving) {
            this.props.store.shippingListUpload.submit();
        }
    };
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./app/web/src/routes/internal/ShippingListUpload.tsx